import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Factory, Building2, Network, Users, Hammer, CheckCircle2,
  Sparkles,
} from "lucide-react";

const AUDIENCES = [
  {
    id: "manufacturers",
    icon: Factory,
    label: "For Manufacturers",
    tag: "Primary Audience",
    accent: "#C62828",
    lead: "Turn contractor demand into measurable channel sell-through.",
    body: "You spend millions creating demand. LumberLoop USA finally lets you see what happens after contractors ask about your products — dealer response, quote activity, win rate, lost-reason. Marketing ROI becomes measurable at the SKU and ZIP level.",
    points: [
      "Route contractor demand to your approved dealer network",
      "See dealer response times and conversion in real time",
      "Understand product demand by region and category",
      "Support high-performing dealers with real data",
      "Measure the actual ROI of every marketing dollar",
    ],
    cta: { label: "Manufacturer page", to: "/manufacturers" },
  },
  {
    id: "dealers",
    icon: Building2,
    label: "For Dealers",
    tag: "Approved Partner Network",
    accent: "#17324D",
    lead: "Contractor leads you didn't have to go find.",
    body: "When a contractor in your territory asks about a manufacturer you carry, LumberLoop USA routes that request directly to you — free of charge, sponsored by the manufacturer. Your team quotes. Your team closes.",
    points: [
      "Free to participate — sponsored by manufacturer partners",
      "Only leads in your territory and product lines",
      "Contractor contact info, specs, quantity, and timeline",
      "You own the quote, the customer, and the transaction",
      "Stronger data-backed manufacturer partnerships",
    ],
    cta: { label: "Details for Dealers", to: "/solutions/dealers" },
  },
  {
    id: "distributors",
    icon: Network,
    label: "For Distributors",
    tag: "Multi-Brand Visibility",
    accent: "#334155",
    lead: "Every brand. Every territory. One workspace.",
    body: "Distributors sit at the center of the building materials channel — but usually get the least visibility. LumberLoop USA gives you a unified view of contractor demand across every manufacturer partner and every dealer in your region.",
    points: [
      "Cross-brand dashboards in one workspace",
      "Regional demand heat maps",
      "Category and product trend intelligence",
      "Dealer performance across your downstream network",
      "Sell-through signals 30–60 days ahead of PO",
    ],
    cta: { label: "Details for Distributors", to: "/solutions/distributors" },
  },
  {
    id: "sales-reps",
    icon: Users,
    label: "For Sales Reps",
    tag: "Field Intelligence",
    accent: "#0F766E",
    lead: "Walk into every dealer call with real numbers.",
    body: "LumberLoop USA turns your territory into a live dashboard. Which dealers are winning. Which are missing quotes. Where contractors are asking for your line — and where they aren't. Show up prepared.",
    points: [
      "Territory demand heat maps",
      "Dealer response scorecards",
      "Lost-reason insights per dealer",
      "New dealer prospecting from uncovered ZIPs",
      "Quarterly business review-ready reporting",
    ],
    cta: { label: "Details for Sales Reps", to: "/solutions/sales-reps" },
  },
  {
    id: "contractors",
    icon: Hammer,
    label: "For Contractors",
    tag: "Free Public Portal",
    accent: "#64748B",
    lead: "Submit once. Get quoted by local approved dealers.",
    body: "Contractors and builders can submit a structured quote request in under 60 seconds — and LumberLoop USA routes it to the manufacturer's approved dealers nearest to the job site. Free, fast, no marketplace middleman.",
    points: [
      "Free to contractors, always",
      "Local, manufacturer-approved dealers only",
      "One form → multiple quotes",
      "No spam, no signup required",
    ],
    cta: { label: "Request a Quote", to: "/request-quote" },
  },
];

export default function SolutionsHub() {
  useEffect(() => {
    document.title = "Solutions — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "LumberLoop USA solutions for every stakeholder in the building materials channel — manufacturers, dealers, distributors, sales reps, and contractors.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="solutions-hub-page" className="bg-white">
      {/* HERO */}
      <section className="relative bg-white border-b border-slate-100">
        <div className="absolute inset-0 grid-backdrop opacity-40 pointer-events-none"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="label-tag justify-center">Solutions</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02] text-[#17324D]">
            Built for everyone in the<br/><span className="text-[#C62828]">building materials channel.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Manufacturers create demand. Dealers close sales. Distributors move product. Sales reps carry the story. Contractors buy. LumberLoop USA connects all of it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary">Request a Demo <ArrowRight size={16}/></Link>
            <a href="#manufacturers" className="btn-secondary">Jump to Manufacturers</a>
          </div>
        </div>
      </section>

      {/* Sticky in-page nav */}
      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap gap-1 py-3 overflow-x-auto">
          {AUDIENCES.map(a => (
            <a key={a.id} href={`#${a.id}`} className="px-3 py-1.5 rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-[#17324D] transition-colors whitespace-nowrap">
              {a.label}
            </a>
          ))}
        </div>
      </div>

      {/* Audience sections */}
      <div className="divide-y divide-slate-100">
        {AUDIENCES.map((a, i) => (
          <AudienceSection key={a.id} audience={a} reverse={i % 2 === 1} primary={a.id === "manufacturers"}/>
        ))}
      </div>

      {/* Final CTA */}
      <section className="relative bg-[#0F2540] overflow-hidden border-t border-slate-800">
        <div className="absolute inset-0 grid-backdrop opacity-25"/>
        <div className="absolute -top-32 left-1/3 w-96 h-96 bg-[#C62828]/10 rounded-full blur-3xl"/>
        <div className="relative max-w-3xl mx-auto px-6 py-20 text-center text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15">
            <Sparkles size={12} className="text-[#C62828]"/>
            <span className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-slate-200">Not sure where you fit?</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-5">Book a 20-min discovery call.</h2>
          <p className="mt-4 text-slate-300">We'll walk you through the platform through your lens.</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 mt-8">Request a Demo <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}

function AudienceSection({ audience, reverse, primary }) {
  const a = audience;
  return (
    <section id={a.id} className={`scroll-mt-40 py-20 lg:py-24 ${primary ? "bg-slate-50/60" : "bg-white"}`} data-testid={`solution-${a.id}`}>
      <div className={`max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md" style={{ background: a.accent }}>
              <a.icon size={22}/>
            </div>
            <span className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-slate-500">{a.tag}</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-5 leading-[1.05] text-[#17324D]">{a.label}</h2>
          <p className="mt-3 text-xl text-slate-700 font-medium">{a.lead}</p>
          <p className="mt-4 text-slate-600 leading-relaxed">{a.body}</p>
          <ul className="mt-6 space-y-2.5">
            {a.points.map(p => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: a.accent }}/>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={a.cta.to} className="btn-secondary">{a.cta.label} <ArrowRight size={14}/></Link>
            <Link to="/contact" className="btn-ghost">Talk to us</Link>
          </div>
        </div>

        <AudienceMock audience={a}/>
      </div>
    </section>
  );
}

function AudienceMock({ audience }) {
  const { id, accent } = audience;
  // Distinct mini-mockup per audience
  if (id === "manufacturers") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-5">
        <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-400 mb-3">Manufacturer Dashboard</div>
        <div className="grid grid-cols-3 gap-2">
          {[["Requests","127","+14%"],["Response","3.2h","-22%"],["Conv.","31%","+6%"]].map(([l,v,d]) => (
            <div key={l} className="rounded-lg border border-slate-200 p-3">
              <div className="text-[9px] font-mono-ll tracking-wider uppercase text-slate-400">{l}</div>
              <div className="font-display text-xl text-[#17324D] leading-none mt-1">{v}</div>
              <div className="text-[9px] text-emerald-600 font-mono-ll mt-1">{d}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] font-mono-ll tracking-wider uppercase text-slate-500 mb-2">Demand Heat Map</div>
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
            {Array.from({length:48}).map((_,i)=>{
              const h = [0.08, 0.2, 0.45, 0.7, 1][Math.floor(Math.random()*5)];
              return <div key={`heat-${i}`} className="aspect-square rounded-sm" style={{ background: `rgba(198,40,40,${h})` }}/>;
            })}
          </div>
        </div>
      </div>
    );
  }
  if (id === "dealers") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-5">
        <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-400 mb-3">New Opportunity Feed</div>
        <div className="space-y-2">
          {[
            { p: "Roofing Underlayment", loc: "Omaha, NE 68154", t: "just now", hot: true },
            { p: "LP SmartSide Trim",     loc: "Denver, CO 80202", t: "12m", hot: false },
            { p: "ZIP System Sheathing",  loc: "Portland, OR 97201", t: "1h", hot: false },
          ].map((it, i) => (
            <div key={it.p} className={`rounded-lg border p-3 flex items-center gap-3 ${it.hot ? "border-blue-200 bg-blue-50/50" : "border-slate-200"}`}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15`, color: accent }}>
                <Building2 size={14}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#17324D] truncate">{it.p}</div>
                <div className="text-xs text-slate-500 truncate">{it.loc}</div>
              </div>
              <div className="text-[10px] font-mono-ll text-slate-400">{it.t}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (id === "distributors") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-5">
        <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-400 mb-3">Multi-Brand Portfolio</div>
        <div className="space-y-2">
          {[
            { b: "Brand A · Roofing", v: 82 },
            { b: "Brand B · Siding",  v: 66 },
            { b: "Brand C · Framing", v: 51 },
            { b: "Brand D · Deck",    v: 38 },
          ].map(r => (
            <div key={r.b} className="text-[11px]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-700 font-medium">{r.b}</span>
                <span className="font-mono-ll text-slate-500">{r.v}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${r.v}%`, background: `linear-gradient(90deg, ${accent}, #17324D)` }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (id === "sales-reps") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-5">
        <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-400 mb-3">Territory · Response Scorecard</div>
        <div className="space-y-2">
          {[
            { d: "Northwest Supply · OR", s: 94, tag: "Fast" },
            { d: "Timber Depot · NC",     s: 88, tag: "Fast" },
            { d: "Cascade Building · WA", s: 71, tag: "OK"   },
            { d: "Rocky Mtn Lumber · CO", s: 52, tag: "Slow" },
          ].map(r => (
            <div key={r.d} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5">
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15`, color: accent }}>
                <Users size={14}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-[#17324D] truncate">{r.d}</div>
                <div className="text-[10px] text-slate-500">Response score</div>
              </div>
              <div className="text-sm font-display text-[#17324D]">{r.s}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // contractors
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-5">
      <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-400 mb-3">Quote Request Form</div>
      <div className="space-y-2">
        {["Product Category","ZIP Code","Quantity & Specs","Timeline"].map((l, i) => (
          <div key={l} className="rounded-lg border border-slate-200 p-2.5 flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">{i+1}</div>
            <div className="text-xs text-slate-700">{l}</div>
            <div className="ml-auto text-[10px] text-emerald-600 font-mono-ll">✓</div>
          </div>
        ))}
        <div className="rounded-lg p-3 text-center mt-3" style={{ background: accent, color: "#fff" }}>
          <div className="text-xs font-bold uppercase tracking-wider">Get Free Quotes</div>
        </div>
      </div>
    </div>
  );
}
